import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { StyledEngineProvider } from '@mui/material/styles';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { CookiesProvider, useCookies } from 'react-cookie';
import { Client, Provider as QueryProvider, fetchExchange } from 'urql';
import { contentfulGraphQLURL } from '@src/constants';
import { TAGThemeProvider } from '@src/lib/shared-components-web';
import { AppContextProvider } from '@src/lib/shared-components-web/context/AppContextProvider.ctx';
import { FacilityContextProvider } from '@src/lib/shared-components-web/context/FacilityContextProvider.ctx';
import { FacilityContextProvider as FacilityContextProviderSSR } from '@src/lib/shared-components-web/context/FacilityContextProviderSSR.ctx';
import { SSRFacilityContextType } from '@src/lib/shared-components-web/services/useFacilitiesSSR';
import { TAGBrand } from '@src/lib/shared-components-web/types';
const queryClient = new Client({
  url: contentfulGraphQLURL,
  exchanges: [fetchExchange],
  requestPolicy: 'network-only',
  fetchOptions: () => {
    return {
      headers: {
        Authorization: `Bearer ${process.env.TAG_CONTENTFUL_ACCESS_TOKEN}`,
        'content-type': 'application/json'
      }
    };
  }
});
const previewQueryClient = new Client({
  url: contentfulGraphQLURL,
  exchanges: [fetchExchange],
  requestPolicy: 'network-only',
  fetchOptions: () => {
    return {
      headers: {
        Authorization: `Bearer ${process.env.TAG_CONTENTFUL_PREVIEW_ACCESS_TOKEN}`,
        'content-type': 'application/json'
      }
    };
  }
});
type ContextProvidersProps = {
  config: any;
  preview: boolean;
  environment: string;
  user: any;
  children: ReactNode;
  pageTheme?: any;
  defaultToCdn?: boolean;
  deviceType?: string;
  hideHeaderFacilityLocator?: boolean;
  stateCodeFromHeaders?: string;
  facilityCode?: string;
  facilityContext?: SSRFacilityContextType | null;
};
export const ContextProviders = ({
  config,
  preview,
  environment,
  user,
  children,
  pageTheme,
  defaultToCdn = false,
  deviceType,
  hideHeaderFacilityLocator = false,
  stateCodeFromHeaders,
  facilityCode,
  facilityContext
}: ContextProvidersProps) => {
  const router = useRouter();
  const [cookies] = useCookies(['tag-theme']);
  const brandTheme = cookies['tag-theme'] || config?.themeId || process.env.TAG_BRAND_THEME || 'lovet';
  const brandName = brandTheme as TAGBrand;
  const gqlClient = preview ? previewQueryClient : queryClient;
  // When SSR already fetched the facility (e.g. facility detail pages), seed the context with
  // that data instead of triggering a redundant client-side facility fetch.
  const FacilityProviderComponent = facilityContext ? FacilityContextProviderSSR : FacilityContextProvider;
  const themedContent = <TAGThemeProvider branding={brandName} pageTheme={pageTheme} deviceType={(deviceType || 'mobile') as 'desktop' | 'mobile'}>
			<FacilityProviderComponent brand={brandName} user={user} facilityCode={facilityCode} defaultToCdn={defaultToCdn} disableFacilityFetching={hideHeaderFacilityLocator} {...facilityContext ? {
      ssrData: facilityContext
    } : {}}>
				{children ?? null}
			</FacilityProviderComponent>
		</TAGThemeProvider>;
  return <QueryProvider value={gqlClient} data-sentry-element="QueryProvider" data-sentry-component="ContextProviders" data-sentry-source-file="ContextProviders.tsx">
			<ContentfulLivePreviewProvider locale={router.locale || 'en-US'} enableInspectorMode={preview} enableLiveUpdates={preview} targetOrigin={['https://app.contentful.com', 'https://app.eu.contentful.com']} data-sentry-element="ContentfulLivePreviewProvider" data-sentry-source-file="ContextProviders.tsx">
				<CookiesProvider data-sentry-element="CookiesProvider" data-sentry-source-file="ContextProviders.tsx">
					<AppContextProvider config={config} theme={brandName} environment={environment as 'local' | 'dev' | 'stage' | 'prod'} appName={process.env.APP_NAME || 'tag_dev'} appVersion={`${process.env.APP_VERSION || 'unknown'}(design-system:${process.env.DESIGN_SYSTEM_VERSION})`} stateCodeFromHeaders={stateCodeFromHeaders} data-sentry-element="AppContextProvider" data-sentry-source-file="ContextProviders.tsx">
						<StyledEngineProvider injectFirst data-sentry-element="StyledEngineProvider" data-sentry-source-file="ContextProviders.tsx">{themedContent}</StyledEngineProvider>
					</AppContextProvider>
				</CookiesProvider>
			</ContentfulLivePreviewProvider>
		</QueryProvider>;
};